import { styled } from "@linaria/react"
import { observer } from "mobx-react-lite"

import { Button } from "@/components/Button/Button.tsx"
import { Horizontal, Spring, Vertical } from "@/components/Flex.ts"
import { Heading2 } from "@/components/Heading/Heading.tsx"
import { Input } from "@/components/Input/Input.tsx"
import { Spinner } from "@/components/Spinner/Spinner.tsx"
import { ellipsize, smallFontSize } from "@/styles/textStyles.ts"

import type { ProjectRootFolderViewModel } from "@/components/ProjectSettingsDialog/ProjectRootFolderViewModel.ts"


export const ProjectRootFolderTab = observer((props: {
  model: ProjectRootFolderViewModel
}) => {
  const vm = props.model

  return (
    <Vertical gap="12px">
      <Heading2>Root Folder</Heading2>

      <FieldLabel>
        This is the main folder where the AI agent will work. It won't look at or change anything outside this folder.
      </FieldLabel>

      <Horizontal gap="8px">
        {vm.isLoading.get()
          ? <Spinner text={ellipsize("Loading")} />
          : (
            <Input
              placeholder="e.g. packages/app"
              value={vm.value.get()}
              onChange={(e) => vm.value.set(e.currentTarget.value)}
              disabled={!vm.canEditProject.get() || vm.isSaving.get() || vm.isReadonly.get()}
            />
          )}
      </Horizontal>

      {!vm.projectConfigured.get() && (
        <FieldLabel>
          The project is not configured yet. Complete the preview setup process first.
        </FieldLabel>
      )}

      {vm.projectConfigured.get() && vm.isReadonly.get() && (
        <FieldLabel>
          Matter project configuration is defined in the runtime config file stored in the repository's default branch
          and can't be modified here.
        </FieldLabel>
      )}

      {vm.hasChanges.get() && !vm.isReadonly.get() && (
        <ButtonsRow>
          <Spring />

          <Button kind="alternative" onClick={vm.discard} data-testid="project-settings-cancel">
            Cancel
          </Button>

          <Button
            kind="primary"
            onClick={vm.onSave}
            disabled={vm.isSaving.get()}
            data-testid="save-button"
          >
            {vm.isSaving.get() ? ellipsize("Saving") : "Save"}
          </Button>
        </ButtonsRow>
      )}

      {vm.error.get() && (
        <ErrorText>{vm.error.get()}</ErrorText>
      )}
    </Vertical>
  )
})

const FieldLabel = styled.div`
  color: var(--secondary-text-color);

  ${smallFontSize};
`

const ButtonsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ErrorText = styled.div`
  color: var(--error-color);

  ${smallFontSize};
`
